<template>
  <v-dialog :value="value" max-width="500px" persistent>
    <v-card>
      <v-card-title> {{ translations.cloneScheduleTitle }}</v-card-title>
      <v-card-text>
        <v-container>
          <section class="d-flex justify-space-between flex-column">
            <v-date-picker width="auto" tag="div" flat v-model="cloneDate" locale="it" range></v-date-picker>
          </section>
        </v-container>

        <!-- project selection -->
        <v-divider v-if="showProjectWhenCloning"></v-divider>
        <v-container class="d-flex align-center" v-if="showProjectWhenCloning">
          <div class="project-schedule mt-2">
            <h3 class="mb-2">{{ translations.projectSelect }}</h3>
            <v-row no-gutters class="d-flex align-center justify-start">
              <v-col cols="10">
                <v-menu close-on-click close-on-content-click
                    offset-y allow-overflow height="35px" style="width:300px; font-size: 14px">
              <template v-slot:activator="{ on }">
                <v-text-field v-on="on" v-model="projectToClone"
                              @input="projectFilterHandler"
                              outlined hide-details autocomplete="off"
                              class="f-12" height="35px" style="width:300px; font-size: 14px">
                </v-text-field>
              </template>
              <div style="max-height: 300px" class="scroll-bar">
                <v-list dense>
                  <template>
                    <v-list-item v-for="(filteredProject, filteredProjectIndex) in availableProjects" @click="changeProjectHandler(filteredProject)" :key="filteredProjectIndex">
                      <v-list-item-content>
                        <v-list-item-title>{{ filteredProject.keyCode }}</v-list-item-title>
                        <v-list-item-subtitle>{{ filteredProject.label }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item v-if="!isLoadingProjects && hasNextProjects">
                      <div class="d-flex align-center justify-center" style="width: 100%">
                        <v-progress-circular v-intersect.quiet="onIntersect" indeterminate color="main" size="24"></v-progress-circular>
                      </div>
                    </v-list-item>
                  </template>
                </v-list>
              </div>
                </v-menu>
              </v-col>
              <v-col cols="2">
                <template>
                  <v-btn @click="removeMainProject" icon>
                    <v-icon color="red">mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <p v-if="projectLabel" class="pa-0 ma-0 pt-1 grey--text" style="font-size: 14px">
                {{ projectLabel }}
              </p>
            </v-row>

            <!-- part selection -->
            <v-row v-if="parts.length" no-gutters class="d-flex align-center justify-start">
              <div class="d-flex">
                <div class="mb-2 mr-2">
                  <label>{{ translations.part }}</label>
                  <v-select v-model="partToClone" :items="parts" @change="updatePart" item-text="keyCode" clearable @click:clear="clearPart" dense style="font-size: 14px" outlined append-icon="" height="35px" hide-details return-object>
                    <template v-slot:item="data">
                      <span>{{ `${data.item.keyCode} - ${data.item.description}` }}</span>
                    </template>
                  </v-select>
                  <p v-if="partToClone && partToClone.description" class="pa-0 ma-0 pt-1 grey--text" style="font-size: 14px">
                    {{ partToClone.description }}
                  </p>
                </div>
              </div>
            </v-row>
          </div>
        </v-container>
      </v-card-text>
      <v-card-actions style="border-top: 1px solid rgba(125, 125, 125, 0.2)">
        <v-btn color="red darken-1" text @click="$emit('input', false), resetMainProject()">
          {{ translations.close }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1 white--text" @click="submit(), resetMainProject()" :disabled="isSubmitDisabled">
          {{ translations.submit }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { translations } from '../utils/common'
import { mapState } from 'vuex'
import debounce from "lodash/debounce"
import { cloneDeep } from 'lodash'
import { getScheduleShowProjectSelectionOnClone } from '@/utils/api/config'

export default {
  props: {
    value: {
      type: Boolean,
      default: () => false
    },

    startValue: {
      type: String,
      required: true
    },

    mainProject: {
      type: String,
      required: false
    },

    availableProjects: {
      type: Array,
      default: () => []
    },

    hasNextProjects: {
      type: Boolean,
      default: () => false
    },

    isLoadingProjects: {
      type: Boolean,
      default: () => false
    },

    mainProjectId: {
      type: Number,
      default: () => 0
    },

    mainProjectLabel: {
      type: String,
      required: false
    },

    mainPartId: {
      type: Number,
      default: () => null
    },

    mainProjectParts: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      cloneDate: null,
      projectToClone: '',
      projectIdToClone: null,
      partToClone: null,
      partIdToClone: null,
      projectId: 0,
      partId: 0,
      showProjectWhenCloning: false,
      projectLabel: '',
      parts: []
    }
  },

  created() {
    this.cloneDate = [this.startValue]
    this.showProjectWhenCloning = getScheduleShowProjectSelectionOnClone()
    this.projectIdToClone = cloneDeep(this.mainProjectId)
    this.projectToClone = cloneDeep(this.mainProject)
    this.projectLabel = cloneDeep(this.mainProjectLabel)
    this.projectId = this.mainProjectId
    this.partId = this.mainPartId
    this.partToClone = this.mainProjectParts.length ? this.mainProjectParts.find(p => p.id === this.mainPartId).keyCode : null
    this.partIdToClone = cloneDeep(this.mainPartId)
    this.parts = this.mainProjectParts
  },

  methods: {
    submit() {
      // check if main project was updated
      if (this.projectIdToClone === this.projectId && ((!this.partId && !this.partIdToClone) || (this.partIdToClone && this.partId && this.partIdToClone === this.partId))) {
        this.projectIdToClone = null
        this.partIdToClone = null
      }

      this.$emit('submit', {
        cloneDatesRange: this.getRangeDates,
        projectId: this.projectIdToClone,
        partId: this.partIdToClone
      })

      this.dialog = false

      this.$emit('input', false)
    },

    projectFilterHandler: debounce(function(e) {
      return this.$emit('filter-projects', e)
    }, 850),

    onIntersect(entries, observer, isIntersecting) {
      if (isIntersecting) {
        return this.$emit('fetch-more-projects')
      }
    },

    changeProjectHandler(project) {
      if (!project) return

      this.projectToClone = project.keyCode
      this.projectIdToClone = project.id
      this.projectLabel = project.label ? project.label : ''
      this.parts = project.parts
      this.partIdToClone = null
      this.partToClone = null
    },

    async removeMainProject() {
      this.projectToClone = ''
      this.projectIdToClone = 0
      this.projectLabel = ''
      this.partToClone = null
      this.partIdToClone = null
      this.parts = []
    },

    resetMainProject() {
      this.projectIdToClone = cloneDeep(this.mainProjectId)
      this.projectToClone = cloneDeep(this.mainProject)
      this.projectLabel = cloneDeep(this.mainProjectLabel)
      this.partToClone = this.mainProjectParts.length ? this.mainProjectParts.find(p => p.id === this.mainPartId).keyCode : null
      this.partIdToClone = cloneDeep(this.mainPartId)
      this.parts = this.mainProjectParts
    },

    clearPart() {
      this.partToClone = null
      this.partIdToClone = null
    },

    updatePart(value) {
      this.partIdToClone = value.id
    }
  },

  computed: {
    translations: () => translations,

    ...mapState({
      todaysDate: state => state.date.todaysDate
    }),

    getRangeDates() {
      if (this.cloneDate.length === 1) {
        return [...this.cloneDate]
      }

      const sortedDates = [...this.cloneDate].sort()

      let datesRange = []
      let startDate = new Date(sortedDates.at(0))
      let endDate = new Date(sortedDates.at(sortedDates.length - 1))

      while (startDate <= endDate) {
        datesRange.push(startDate.toISOString().slice(0, 10))
        startDate.setDate(startDate.getDate() + 1)
      }

      return datesRange
    },

    isSubmitDisabled() {
      return this.cloneDate.length === 0 || this.getRangeDates[0] < this.todaysDate
    }
  },

  watch: {
    mainProject(project) {
      this.projectToClone = cloneDeep(project)
    },

    mainProjectId(id) {
      this.projectIdToClone = cloneDeep(id)
      this.projectId = id
    },

    mainProjectLabel(label) {
      this.projectLabel = cloneDeep(label)
    },

    mainPartId(id) {
      this.partIdToClone = cloneDeep(id)
      this.partId = id
      this.partToClone = this.mainProjectParts.length ? this.mainProjectParts.find(p => p.id === this.mainPartId).keyCode : null
    },

    mainProjectParts(list) {
      this.parts = list
    }
  }
}
</script>

<style>
.project-schedule .v-text-field .v-input__control .v-input__slot {
  min-height: auto !important;
  display: flex !important;
  align-items: center !important;
}

</style>
